// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { checkboxSizes, CheckboxStyle } from "@insite/mobius/Checkbox";
import CheckboxPresentationPropsDefault from "@insite/mobius/Checkbox/presentationProps";
import { StyledClickable } from "@insite/mobius/Link";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import getColor from "@insite/mobius/utilities/getColor";
import getContrastColor from "@insite/mobius/utilities/getContrastColor";
import resolveColor from "@insite/mobius/utilities/resolveColor";
import Check from "@mcmc/XDL/Icons/Check";
import Minus from "@mcmc/XDL/Icons/Minus";
import { xBaseTheme } from "@mcmc/XDL/types";
import { css } from "styled-components";

checkboxSizes.default = {
    fontSize: 14,
    borderRadius: 2,
    iconSize: 16,
};

const commonIconStyles = {
    size: 12,
    css: css`
        height: 16px;
        width: 16px;
        justify-content: center;
    `,
};

export default {
    defaultProps: {
        iconProps: {
            src: Check,
            ...commonIconStyles,
        },
        indeterminateIconProps: {
            src: Minus,
            ...commonIconStyles,
        },
        color: "primary",
        typographyProps: {
            disabledColor: "text",
            css: css`
                word-break: normal;
                line-height: 18px;
            `,
        },
        css: css`
            align-items: flex-start;
            ${({ disable, disabled }: any) => (disable || disabled) && `opacity:0.5`};

            ${StyledClickable} {
                display: inline;
            }

            &:hover {
                span[role="checkbox"]:not([aria-disabled="true"]) {
                    background: ${getColor("secondary")};
                    &[aria-checked="true"],
                    &[aria-checked="mixed"] {
                        background: ${getColor("primary")};
                        svg {
                            visibility: hidden;
                        }
                    }
                }
            }

            span[role="checkbox"] {
                & + label {
                    margin-left: 5px;
                }

                &[aria-disabled="true"] {
                    border: 1px solid ${getColor("common.border")};
                    &[aria-checked="true"],
                    &[aria-checked="mixed"] {
                        background: ${({ _color, theme }: any) => resolveColor(_color, theme)};
                        color: ${({ _color, theme }: any) => getContrastColor(_color, theme)};
                        border: 0;
                    }
                }

                &:focus {
                    box-shadow: none;
                    &:not([aria-disabled="true"]) {
                        box-shadow: 0 0 0 2px ${getColor("text")};
                    }
                }
            }
        `,
    },
    groupDefaultProps: {
        labelProps: {
            size: "14px",
            lineHeight: "16px",
            weight: 700,
            css: css`
                &:empty {
                    display: none;
                }
            `,
        },
        errorProps: {
            size: "12px",
            lineHeight: "14px",
            weight: "normal",
            css: css`
                &:first-letter {
                    text-transform: uppercase;
                }
                &:empty {
                    display: none;
                }
            `,
        },
        css: css`
            ${({ disabled }: any) => disabled && `opacity:0.5`};
            & > ${TypographyStyle as any} {
                padding: 0;
                &:first-child {
                    margin-bottom: 15px;
                }
                &:last-child {
                    margin-top: 5px;
                }
            }
            ${/* sc-selector */ CheckboxStyle} + ${/* sc-selector */ CheckboxStyle} {
                margin-top: 5px;
            }

            &.inline-group {
                display:flex;
                flex-direction: row;
                flex-wrap: wrap;
                margin: -5px -10px;

                & > ${TypographyStyle as any} {
                    flex-basis: 100%;
                    padding: 5px 10px;

                    &:first-child {
                        margin-bottom: 10px;
                    }
                    
                    &:last-child {
                        padding-top: 0;
                    }
                }
                
                ${/* sc-selector */ CheckboxStyle} + ${/* sc-selector */ CheckboxStyle} {
                   margin-top: 0px;
                }

                ${/* sc-selector */ CheckboxStyle} {
                    padding: 5px 10px;

                }

            }
        `,
    },
} as xBaseTheme["checkbox"];
